import React from 'react';
import { Eye, TrendingUp, Users, Target, ChevronRight, Star, ArrowRight, Play, Shield, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
const InforCRMComponent = () => {
  const benefits = [
    {
      id: 1,
      icon: <Eye className="w-8 h-8" />,
      title: "360-Degree Customer View for Smarter Engagement",
      description: "Comprehensive, centralized view of every customer interaction across departments. Unified customer profiles enable faster responses, informed decisions, and personalized communications.",
      highlights: ["Shared customer profiles", "Real-time data access", "Seamless experience"],
      primaryColor: "#2563eb",
      lightColor: "#dbeafe",
      image: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=400&h=300&fit=crop&crop=center"
    },
    {
      id: 2,
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Sales Productivity and Pipeline Visibility",
      description: "Boost sales performance with real-time pipeline visibility. Track leads, opportunities, and quotes from a single dashboard with built-in forecasting tools.",
      highlights: ["Pipeline management", "Mobile access", "Performance tracking"],
      primaryColor: "#dc2626",
      lightColor: "#fee2e2",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop&crop=center"
    },
    {
      id: 3,
      icon: <Users className="w-8 h-8" />,
      title: "Enhanced Customer Service and Support",
      description: "Deliver proactive, high-quality customer service with consolidated service requests and case histories. Transform reactive service into strategic advantage.",
      highlights: ["Case management", "SLA tracking", "Knowledge bases"],
      primaryColor: "#16a34a",
      lightColor: "#dcfce7",
      image: "https://images.unsplash.com/photo-1556740749-887f6717d7e4?w=400&h=300&fit=crop&crop=center"
    },
    {
      id: 4,
      icon: <Target className="w-8 h-8" />,
      title: "Targeted Marketing and Campaign Optimization",
      description: "Launch highly targeted campaigns with marketing automation tools. Leverage rich customer data for audience segmentation and personalized messaging.",
      highlights: ["Campaign automation", "Lead scoring", "Real-time analytics"],
      primaryColor: "#ea580c",
      lightColor: "#fed7aa",
      image: "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=400&h=300&fit=crop&crop=center"
    }
  ];

  const stats = [
    { value: "360°", label: "Customer View", color: "#2563eb" },
    { value: "25%", label: "Sales Increase", color: "#dc2626" },
    { value: "40%", label: "Faster Resolution", color: "#16a34a" },
    { value: "60%", label: "Campaign ROI", color: "#ea580c" }
  ];

  const features = [
    { icon: <Shield className="w-5 h-5" />, text: "Enterprise Security" },
    { icon: <Zap className="w-5 h-5" />, text: "Real-time Analytics" },
    { icon: <Users className="w-5 h-5" />, text: "Team Collaboration" }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#f9fafb" }}>
      {/* Enhanced Hero Section */}
      <div className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #1f2937 0%, #374151 100%)" }}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" 
               style={{ backgroundImage: "radial-gradient(circle at 25% 25%, #ffffff 2px, transparent 2px)", backgroundSize: "60px 60px" }}>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 py-24 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium border" 
                   style={{ backgroundColor: "#374151", borderColor: "#4b5563", color: "#f9fafb" }}>
                <Star className="w-4 h-4 mr-2" style={{ color: "#fbbf24" }} />
                Enterprise CRM Solution
              </div>
              
              <div className="space-y-6">
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight" style={{ color: "#ffffff" }}>
                  Transform Your
                  <span className="block" style={{ color: "#60a5fa" }}>Customer</span>
                  <span className="block" style={{ color: "#34d399" }}>Experience</span>
                </h1>
                
                <p className="text-xl leading-relaxed max-w-xl" style={{ color: "#d1d5db" }}>
                  Empowering PTKCS with intelligent CRM solutions that drive growth, enhance service, and maximize customer engagement through advanced analytics and automation.
                </p>
              </div>

              {/* Feature Pills */}
              <div className="flex flex-wrap gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center px-4 py-2 rounded-full" 
                       style={{ backgroundColor: "#374151", color: "#e5e7eb" }}>
                    <span style={{ color: "#60a5fa" }}>{feature.icon}</span>
                    <span className="ml-2 text-sm font-medium">{feature.text}</span>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="inline-flex items-center px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 hover:transform hover:scale-105 hover:shadow-xl group" 
                        style={{ backgroundColor: "#2563eb", color: "#ffffff" }}>
                  Get Started Now
                  <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
                </button>
                
                <button className="inline-flex items-center px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 hover:transform hover:scale-105 border-2 group" 
                        style={{ borderColor: "#4b5563", color: "#ffffff", backgroundColor: "transparent" }}>
                  <Play className="mr-2 w-5 h-5" />
                  Watch Demo
                </button>
              </div>
            </div>

            {/* Right Content - Hero Image */}
            <div className="relative">
              <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-700">
                <img 
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop&crop=center" 
                  alt="CRM Dashboard"
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(45deg, rgba(37, 99, 235, 0.2), rgba(16, 163, 74, 0.2))" }}></div>
              </div>
              
              {/* Floating Stats */}
              <div className="absolute -top-4 -left-4 px-6 py-4 rounded-xl shadow-lg backdrop-blur-sm" 
                   style={{ backgroundColor: "rgba(255, 255, 255, 0.95)" }}>
                <div className="text-2xl font-bold" style={{ color: "#16a34a" }}>98%</div>
                <div className="text-sm" style={{ color: "#6b7280" }}>Client Satisfaction</div>
              </div>
              
              <div className="absolute -bottom-4 -right-4 px-6 py-4 rounded-xl shadow-lg backdrop-blur-sm" 
                   style={{ backgroundColor: "rgba(255, 255, 255, 0.95)" }}>
                <div className="text-2xl font-bold" style={{ color: "#dc2626" }}>10M+</div>
                <div className="text-sm" style={{ color: "#6b7280" }}>Data Points</div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave Separator */}
        <div className="absolute bottom-0 left-0 w-full">
          <svg className="w-full h-16" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,120 L1200,120 L1200,20 C1000,80 800,80 600,20 C400,80 200,80 0,20 Z" 
                  fill="#f9fafb" fillOpacity="1"></path>
          </svg>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-20" style={{ backgroundColor: "#ffffff" }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 rounded-full text-sm font-semibold mb-6" 
                 style={{ backgroundColor: "#f3f4f6", color: "#374151" }}>
              ✨ Key Benefits
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: "#111827" }}>
              Transform Your Business Operations
            </h2>
            <p className="text-xl max-w-3xl mx-auto" style={{ color: "#6b7280" }}>
              Discover how Infor CRM delivers measurable value across every department with cutting-edge technology and intuitive design
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => (
              <div key={benefit.id} 
                   className="group relative rounded-3xl overflow-hidden transition-all duration-500 hover:transform hover:scale-[1.02] hover:shadow-2xl"
                   style={{ backgroundColor: "#ffffff", boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}>
                
                {/* Card Header with Image */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={benefit.image} 
                    alt={benefit.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0" 
                       style={{ background: `linear-gradient(45deg, ${benefit.primaryColor}22, ${benefit.primaryColor}44)` }}>
                  </div>
                  
                  {/* Icon Overlay */}
                  <div className="absolute top-6 right-6 p-3 rounded-xl backdrop-blur-sm" 
                       style={{ backgroundColor: "rgba(255, 255, 255, 0.95)" }}>
                    <span style={{ color: benefit.primaryColor }}>{benefit.icon}</span>
                  </div>
                </div>

                <div className="p-8">
                  {/* Badge */}
                  <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold mb-4" 
                       style={{ backgroundColor: benefit.lightColor, color: benefit.primaryColor }}>
                    BENEFIT 0{benefit.id}
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold mb-4 leading-tight" style={{ color: "#111827" }}>
                    {benefit.title}
                  </h3>

                  {/* Description */}
                  <p className="text-base leading-relaxed mb-6" style={{ color: "#6b7280" }}>
                    {benefit.description}
                  </p>

                  {/* Highlights */}
                  <div className="space-y-3 mb-8">
                    {benefit.highlights.map((highlight, idx) => (
                      <div key={idx} className="flex items-center">
                        <div className="w-2 h-2 rounded-full mr-3" style={{ backgroundColor: benefit.primaryColor }}></div>
                        <span className="text-sm font-medium" style={{ color: "#374151" }}>{highlight}</span>
                      </div>
                    ))}
                  </div>

                  {/* Learn More Link */}
                  <div className="flex items-center text-sm font-semibold group-hover:translate-x-2 transition-all duration-300 cursor-pointer" 
                       style={{ color: benefit.primaryColor }}>
                    Explore Feature
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </div>
                </div>

                {/* Accent Border */}
                <div className="absolute top-0 left-0 w-full h-2" 
                     style={{ backgroundColor: benefit.primaryColor }}>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Enhanced Stats Section */}
      <div className="py-20" style={{ backgroundColor: "#f9fafb" }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold mb-4" style={{ color: "#111827" }}>
              Proven Results That Matter
            </h3>
            <p className="text-lg" style={{ color: "#6b7280" }}>
              Real metrics from companies using Infor CRM
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center p-8 rounded-2xl transition-all duration-300 hover:transform hover:scale-105"
                   style={{ backgroundColor: "#ffffff", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)" }}>
                <div className="text-4xl font-bold mb-3" style={{ color: stat.color }}>{stat.value}</div>
                <p className="text-base font-semibold" style={{ color: "#374151" }}>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Enhanced CTA Section */}
      <div className="py-20" style={{ backgroundColor: "#ffffff" }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="rounded-3xl p-12 relative overflow-hidden" 
               style={{ background: "linear-gradient(135deg, #111827 0%, #374151 100%)" }}>
            
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-full h-full" 
                   style={{ backgroundImage: "radial-gradient(circle at 50% 50%, #ffffff 1px, transparent 1px)", backgroundSize: "30px 30px" }}>
              </div>
            </div>
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-bold mb-6" style={{ color: "#ffffff" }}>
                Ready to Transform Your
                <span className="block" style={{ color: "#60a5fa" }}>Customer Relationships?</span>
              </h2>
              <p className="text-lg mb-8 max-w-2xl mx-auto" style={{ color: "#d1d5db" }}>
                Join PTKCS in leveraging Infor CRM's powerful capabilities to drive growth, enhance customer satisfaction, and streamline operations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                
                <Link to="/contact">
                <button className="px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 hover:transform hover:scale-105 hover:shadow-xl"
                        style={{ backgroundColor: "#16a34a", color: "#ffffff" }}>
                  Get Started Today
                </button>
                
                </Link>
                
               
              </div>
            </div>
          </div>
        </div>
      </div>

    
    </div>
  );
};

export default InforCRMComponent;